import styled from "@emotion/styled";
import { CSSObject, Global, Theme } from "@emotion/react";
import airHugSVG from "assets/images/airhug.svg";
import groupHugSVG from "assets/images/grouphug.svg";
import hedgeHugSVG from "assets/images/hedgehug.svg";
import eyesSVG from "assets/images/roadmap_culture.svg";
import mirrorSVG from "assets/images/roadmap_merch.svg";
import hammerSVG from "assets/images/roadmap_tools.svg";
import joinSVG from "assets/images/join-in-on-the-hug.svg";
import sparklesSVG from "assets/images/sparkles.svg";
import yourRoadmapSVG from "assets/images/your-roadmap.svg";
import { FlexRow, H2, H3, Link, SiteContainer, Text } from "atoms";
import { constants } from "config";
import { Head } from "features/misc";

const global = (theme: Theme) => ({
  "html,body": {
    background: theme.colors.pink,
  },
});

function ForCreators() {
  return (
    <Main>
      <Head title="For Creators" />

      <SiteContainer>
        <Global styles={global} />

        <Header>
          <img src={joinSVG} alt="Join in on the Hug" />

          <div>
            <Subtitle>
              The only Web3 accelerator designed for creators, by&nbsp;creators.
            </Subtitle>

            <Text>
              We offer three different programs to meet you where you&apos;re
              at. Get access to the leading creative minds in Web3 to level up
              yourself for long&#8288;-&#8288;term&nbsp;success.
            </Text>

            {constants.applicationsOpen ? (
              <>
                <ApplyLink
                  href={constants.applyUrl}
                  arrow="dr"
                  variant="gradient"
                >
                  Apply Now
                </ApplyLink>

                <Text>
                  Still feeling fuzzy? <Link to="/faq">See our FAQ</Link>.
                </Text>
              </>
            ) : (
              <>
                <ApplyLink to="/faq" arrow="dr" variant="gradient">
                  See our FAQ
                </ApplyLink>

                <Text size="xs">
                  <strong>Applications opening soon!</strong>
                </Text>
              </>
            )}
          </div>
        </Header>

        <Programs>
          <Program>
            <img src={groupHugSVG} alt="" aria-hidden="true" />

            <H2>groupHUG</H2>

            <Text>
              A growth accelerator for post-mint NFT projects who have found
              product-community fit.
            </Text>
            <Text>
              <strong>Is this for me?</strong>
            </Text>
            <Text size="xs">
              You have already gone through a minting process, built a
              community, but are in search of additional support and direction
              on how to create long-term success in the Web3 space.
            </Text>

            <Link variant="gradient" size="xs" to="/faq#grouphug">
              groupHUG FAQs
            </Link>
          </Program>

          <Program>
            <img src={hedgeHugSVG} alt="" aria-hidden="true" />

            <H2>hedgeHUG</H2>

            <Text>
              A hands-on advisory program for pre-mint NFT projects with an
              innovative business&nbsp;idea.
            </Text>
            <Text>
              <strong>Is this for me?</strong>
            </Text>
            <Text size="xs">
              You and your founding team are committed to launching a NFT
              project in the next 3-6 months, and are seeking mentorship,
              advice, and exposure in the lead-up to your&nbsp;mint.
            </Text>

            <Link variant="gradient" size="xs" to="/faq#hedgehug">
              hedgeHUG FAQs
            </Link>
          </Program>

          <Program>
            <img src={airHugSVG} alt="" aria-hidden="true" />

            <H2>airHUG</H2>

            <Text>
              A sponsored artist-in-residence program for up-and-coming artists
              of any&nbsp;medium.
            </Text>
            <Text>
              <strong>Is this for me?</strong>
            </Text>
            <Text size="xs">
              You are an artist of any kind — illustrator, photographer, poet,
              musician — and are looking for guidance on how to grow your brand
              and build out your artistic business in&nbsp;Web3.
            </Text>

            <Link variant="gradient" size="xs" to="/faq#airhug">
              airHUG FAQs
            </Link>
          </Program>
        </Programs>

        <Roadmap>
          <img src={yourRoadmapSVG} alt="Your Roadmap" />

          <FirstSteps alignItems="flex-start">
            <Step>
              <img src={eyesSVG} alt="" aria-hidden="true" />
              <H2>
                Step 1
                <br />
                <small>
                  <strong>Apply to our next cohorts</strong>
                </small>
              </H2>
              <Text>
                Submit an application, which is due&nbsp;
                {constants.applyEndDate}.
              </Text>
            </Step>

            <Step>
              <img src={mirrorSVG} alt="" aria-hidden="true" />
              <H2>
                Step 2
                <br />
                <small>
                  <strong>Interview</strong>
                </small>
              </H2>
              <Text>
                Shortlisted applicants will get a 1:1&nbsp;interview with a HUG
                team&nbsp;member.
              </Text>
            </Step>

            <Step>
              <img src={hammerSVG} alt="" aria-hidden="true" />
              <H2>
                Step 3
                <br />
                <small>
                  <strong>Onboard</strong>
                </small>
              </H2>
              <Text>
                Meet our team, the rest of your cohort, and get ready for HUGs
                in&nbsp;October.
              </Text>
            </Step>
          </FirstSteps>

          <Benefits>
            <H2>
              Program Benefits <small>6 months</small>
            </H2>

            <div>
              <H3 size="md">Level Up</H3>
              <Text>
                An intensive one month curriculum, followed by advances modules
                designed to hold you accountable and position you for long-term
                success as a leader in Web3.
              </Text>

              <H3 size="md">Curated Relationships</H3>
              <Text>
                Partnership and networking opportunities across the HUG
                ecosystem and friendships for life with your cohort and beyond.
              </Text>

              <H3 size="md">PR and Marketing</H3>
              <Text>
                Amplification across HUG&apos;s social channels with an audience
                of over 650K, including peer-to-peer shoutouts, Twitter Spaces,
                and more.
              </Text>

              <H3 size="md">Tools and Resources</H3>
              <Text>
                Access to our growing toolkit, library, and perks for creators,
                including no-fee marketplaces, no-code tools, and pre-mint
                launch platforms.
              </Text>

              <H3 size="md">Tech Consulting</H3>
              <Text>
                Consultations on your development strategy, as well as smart
                contract and Discord security audits from our experienced team
                of developers.
              </Text>

              <H3 size="md">1:1 Advising</H3>
              <Text>
                1:1 calls with HUG&apos;s senior advisors and founding team,
                including Randi Zuckerberg and Debbie Soon.
              </Text>
            </div>

            <H2>
              Alumni Perks <small>Unlimited</small>
            </H2>

            <Text>
              After the 6 months, you will remain a part of our HUGfam forever,
              where you will get continued access to all our tools and
              resources, IRL and virtual alumni mixers, ongoing features on our
              socials and community platforms, and more.
            </Text>

            <img src={sparklesSVG} alt="" aria-hidden="true" />
            <img src={sparklesSVG} alt="" aria-hidden="true" />
          </Benefits>
        </Roadmap>

        <Footer>
          <H2>Ready to get hugged?</H2>

          {constants.applicationsOpen ? (
            <>
              <Link href={constants.applyUrl} arrow="dr" variant="gradient">
                Apply Now
              </Link>

              <Text>
                Still feeling fuzzy? <Link to="/faq">See our FAQ</Link>.
              </Text>
            </>
          ) : (
            <>
              <Link to="/faq" arrow="dr" variant="gradient">
                See our FAQ
              </Link>

              <Text size="md">Applications opening soon!</Text>
            </>
          )}
        </Footer>
      </SiteContainer>
    </Main>
  );
}

const Main = styled.main(({ theme }) => ({
  background: [theme.colors.yellow, theme.gradients.radial2],
  overflow: "hidden",
}));

const Header = styled(FlexRow)({
  flexDirection: "column",
  flexWrap: "nowrap",
  gap: "3rem",
  justifyContent: "center",
  marginBottom: "4rem",
  "&>img": {
    maxWidth: 250,
    width: "90%",
  },
  "&>div": {
    maxWidth: 600,
    textAlign: "center",
    a: {
      marginTop: "0.75rem",
    },
  },
  "@media (min-width: 800px)": {
    flexDirection: "row",
    "&>div": {
      textAlign: "left",
    },
  },
  "@media (min-width: 1000px)": {
    gap: "6rem",
    "&>img": {
      maxWidth: 330,
    },
  },
}).withComponent("header");

const Subtitle = styled(H3)(({ theme }) => ({
  ...theme.fonts.display,
  fontSize: theme.fontSizes.h2,
  marginBottom: "0.75rem",
})).withComponent("p");

const Programs = styled(FlexRow)({
  alignItems: "stretch",
  flexDirection: "column",
  gap: "2rem",
  margin: "0 auto 8rem",
  maxWidth: 1120,
  width: "100%",
  "&>*": {
    flex: 1,
  },
  "@media (min-width: 800px)": {
    flexDirection: "row",
  },
});

const whiteCallout = (theme: Theme): CSSObject => ({
  background: theme.colors.bgLight,
  border: `2px solid ${theme.colors.fg}`,
  borderRadius: theme.borderRadius.sm,
  padding: "2rem",
  textAlign: "center",
  "& > *": {
    margin: 0,
  },
});

const Program = styled(FlexRow)(({ theme }) => ({
  ...whiteCallout(theme),
  flexDirection: "column",
  gap: "0.75rem",
  img: {
    width: 110,
    "@media (min-width: 800px)": {
      width: 150,
    },
  },
  "img+h2": {
    marginTop: "0",
  },
  "&>img, &>p:first-of-type": {
    marginBottom: "1rem",
  },
  a: {
    flex: 0,
  },
})).withComponent("article");

const Roadmap = styled.article({
  "&>img": {
    display: "block",
    margin: "0 auto 3rem",
    maxWidth: 700,
    width: "100%",
  },
});

const FirstSteps = styled(FlexRow)(({ theme }) => ({
  ...whiteCallout(theme),
  background: [
    theme.colors.bgLight,
    `radial-gradient(circle at 50% -60%, ${theme.colors.pink}, ${theme.colors.bgLight} 60%)`,
  ],
  flexDirection: "column",
  gap: "2rem",
  marginBottom: "4rem",
  "&>*": {
    flex: 1,
  },
  "@media (min-width: 800px)": {
    flexDirection: "row",
  },
}));

const Step = styled(FlexRow)(({ theme }) => ({
  flexDirection: "column",
  gap: "0",
  "&>img": {
    display: "block",
    flex: "1 0 auto",
    height: 75,
    marginBottom: "1.5rem",
  },
  small: {
    ...theme.fonts.body,
    display: "block",
    fontSize: theme.fontSizes.sm,
    marginTop: ".5em",
  },
}));

const Benefits = styled.section(({ theme }) => ({
  margin: "0 auto 4rem",
  maxWidth: 900,
  position: "relative",
  width: "100%",
  "@media (min-width: 650px)": {
    width: "80%",
  },
  "&>div": {
    marginBottom: "2rem",
    "@media (min-width: 650px)": {
      columns: 2,
      gap: "2rem",
    },
    p: {
      marginBottom: "2rem",
    },
  },
  "& h2": {
    margin: "0 0 2rem 0",
    small: {
      ...theme.fonts.body,
      display: "block",
      fontSize: theme.fontSizes.md,
      margin: ".5rem 0 0 0",
    },
  },
  "& h3": {
    ...theme.fonts.body,
    fontSize: theme.fontSizes.sm,
    fontWeight: "bold",
    marginBottom: "0.5rem",
  },
  "&>img": {
    position: "absolute",
    "&:first-of-type": {
      top: 0,
      left: "-8rem",
    },
    "&:last-of-type": {
      bottom: 0,
      right: "-8rem",
    },
    "@media (max-width: 650px)": {
      display: "none",
    },
  },
}));

const Footer = styled.footer(({ theme }) => ({
  textAlign: "center",
  h2: {
    fontSize: theme.fontSizes.h1,
    marginBottom: "2rem",
  },
  a: {
    marginBottom: "1rem",
  },
}));

const ApplyLink = styled(Link)(({ theme }) => ({
  marginBottom: theme.spacing.md,
}));

export { ForCreators };
